import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { InputClearButtonComponent } from '@libs/shared/ui/form-fields/input-clear-button/input-clear-button.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, map, Observable, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-search-bar',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatIconModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    InputClearButtonComponent,
  ],
  template: `
    <div class="min-w-60 w-full">
      <mat-form-field
        [floatLabel]="'always'"
        [subscriptSizing]="'dynamic'"
        class="w-full">
        @if (showIcon) {
          <mat-icon matPrefix [svgIcon]="icon" />
        }
        <input
          #keywordInput
          matInput
          type="text"
          [formControl]="searchControl"
          [placeholder]="placeholder"
          (keyup.enter)="onEnter()" />
        <app-input-clear-button matSuffix [control]="searchControl" />
      </mat-form-field>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarComponent implements OnInit, AfterViewInit {
  @Input() debounce = 200;
  @Input() minLength = 2;
  @Input() placeholder: string;
  @Input() icon = 'mat_outline:search';
  @Input() showIcon = true;
  @Input() autoFocus = true;
  @Input() clearSearchKeyword: Observable<void> = new Observable<void>();

  @Input() set focusTrigger(_: string) {
    this.focusOnInput();
  }

  @Output() readonly bySearch = new EventEmitter<string>();
  @Output() readonly byEnterPressed = new EventEmitter<void>();

  @ViewChild('keywordInput') keywordInput: ElementRef;

  searchControl: FormControl = new FormControl();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.clearSearchKeyword
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.focusOnInput();
          setTimeout(() => {
            this.searchControl.reset();
          }, 0);
          this.cdr.markForCheck();
        })
      )
      .subscribe();

    this.searchControl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(this.debounce),
        map(value => value?.trim() || ''),
        tap(keyword => this.bySearch.emit(keyword))
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      // 수정된 부분: autoFocus가 true일 때만 실행
      this.focusOnInput();
    }
  }

  onEnter() {
    this.bySearch.emit(this.searchControl.value);
    this.byEnterPressed.emit();
  }

  focusOnInput() {
    if (this.keywordInput) {
      this.keywordInput.nativeElement.focus();
    }
  }
}
